import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class TabSessionService {
  private readonly lockName = 'gisecloud-active-tab';
  private readonly ownerKey = 'gisecloud-active-tab-owner';
  private readonly tabKey = 'gisecloud-tab-id';
  private readonly reloadAttempts = 20;
  private readonly reloadDelayMs = 100;
  private releaseLock: (() => void) | null = null;
  private ownsLock = false;
  private tabId: string | null = null;

  async acquire(): Promise<boolean> {
    if (this.ownsLock) return true;

    if (!('locks' in navigator)) return this.acquireFallback();

    const tabId = this.getTabId();
    const navigationType = this.getNavigationType();
    const isReload = navigationType === 'reload';
    const owner = this.readOwner();
    const mayBeReloadingOwner = isReload || owner?.tabId === tabId;
    const attempts = mayBeReloadingOwner ? this.reloadAttempts : 1;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (await this.requestLock(tabId)) return true;
      if (attempt + 1 < attempts) await this.delay(this.reloadDelayMs);
    }

    return false;
  }

  release(): void {
    this.releaseLock?.();
    this.releaseLock = null;
    this.ownsLock = false;
  }

  private requestLock(tabId: string): Promise<boolean> {
    let resolveRelease: (() => void) | null = null;
    let resolveResult: ((acquired: boolean) => void) | null = null;
    const release = new Promise<void>((resolve) => {
      resolveRelease = resolve;
    });
    const result = new Promise<boolean>((resolve) => {
      resolveResult = resolve;
    });

    void navigator.locks.request(this.lockName, { ifAvailable: true }, async (lock) => {
      if (!lock) {
        resolveResult?.(false);
        return;
      }

      this.ownsLock = true;
      const lockToken = crypto.randomUUID();
      this.writeOwner(tabId, lockToken);
      this.releaseLock = () => resolveRelease?.();
      resolveResult?.(true);
      await release;

      this.removeOwner(tabId, lockToken);
      this.releaseLock = null;
      this.ownsLock = false;
    });

    return result;
  }

  private getTabId(): string {
    if (this.tabId) return this.tabId;

    const navigationType = this.getNavigationType();
    const savedTabId = sessionStorage.getItem(this.tabKey);
    this.tabId = navigationType === 'reload' && savedTabId ? savedTabId : savedTabId ?? crypto.randomUUID();
    sessionStorage.setItem(this.tabKey, this.tabId);
    return this.tabId;
  }

  private getNavigationType(): string {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return navigation?.type ?? 'navigate';
  }

  private readOwner(): { tabId: string; lockToken: string; acquiredAt: number } | null {
    try {
      const owner = JSON.parse(localStorage.getItem(this.ownerKey) ?? 'null');
      return owner && typeof owner.tabId === 'string' && typeof owner.lockToken === 'string' ? owner : null;
    } catch {
      return null;
    }
  }

  private writeOwner(tabId: string, lockToken: string): void {
    localStorage.setItem(this.ownerKey, JSON.stringify({ tabId, lockToken, acquiredAt: Date.now() }));
  }

  private removeOwner(tabId: string, lockToken: string): void {
    const owner = this.readOwner();
    if (owner?.tabId === tabId && owner.lockToken === lockToken) localStorage.removeItem(this.ownerKey);
  }

  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  private acquireFallback(): boolean {
    const key = `gisecloud-tab-lock:${location.origin}`;
    if (localStorage.getItem(key)) return false;

    localStorage.setItem(key, String(Date.now()));
    this.ownsLock = localStorage.getItem(key) !== null;
    window.addEventListener('pagehide', () => {
      if (this.ownsLock) localStorage.removeItem(key);
    }, { once: true });
    return this.ownsLock;
  }
}
